<script setup lang="ts">
import { ProfileContractorApi } from "../../api";
import { isAxiosError } from "axios";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { computed, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Participant } from "../../types/PersologOnlineTypes.ts";

/**
 * Renders the profiles of the selected participants again without anyone noticing (RDT-497): no e-mail
 * to participant, trainer or additional recipient, no status change. For support, to correct profiles a
 * generator bug rendered wrongly; the corrected PDF is simply there at the next download.
 */
const props = defineProps({
    participants: {
        type: Array as PropType<Participant[]>,
        required: true,
    },
});

const emit = defineEmits<{ done: [] }>();

const { t } = useI18n();
const confirm = useConfirm();
const toast = useToast();

const running = ref(false);
const progress = ref(0);
// The ids of the last run that could not be regenerated, by reason, so support can follow them up.
const skipped = ref<{ noProfile: number[]; inactive: number[]; failed: number[] }>({ noProfile: [], inactive: [], failed: [] });

const count = computed(() => props.participants.length);

const confirmRegenerate = () => {
    confirm.require({
        group: "silent-regenerate",
        header: t("participants.silentRegenerate.confirm.header"),
        message: t("participants.silentRegenerate.confirm.message", { count: count.value }),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: t("participants.silentRegenerate.action"),
        },
        accept: () => regenerate([...props.participants]),
    });
};

/** One participant after the other, so a long list does not crowd out normal generation. */
const regenerate = async (participants: Participant[]) => {
    running.value = true;
    progress.value = 0;
    const result = { requested: 0, noProfile: [] as number[], inactive: [] as number[], failed: [] as number[] };
    for (const participant of participants) {
        try {
            await ProfileContractorApi.regenerateProfileSilently(participant.id!);
            result.requested++;
        } catch (error) {
            const status = isAxiosError(error) ? error.response?.status : undefined;
            if (status === 404) result.noProfile.push(participant.id!);
            else if (status === 409) result.inactive.push(participant.id!);
            else {
                result.failed.push(participant.id!);
                console.error(`Could not regenerate the profile of participant ${participant.id} silently`, error);
            }
        }
        progress.value++;
    }
    running.value = false;
    skipped.value = { noProfile: result.noProfile, inactive: result.inactive, failed: result.failed };
    toast.add({
        severity: result.failed.length ? "error" : result.requested ? "success" : "warn",
        summary: result.failed.length ? t("messages.error") : t("messages.success"),
        detail: t("participants.silentRegenerate.result", {
            requested: result.requested,
            total: participants.length,
        }),
        life: 6000,
    });
    emit("done");
};
</script>

<template>
    <ConfirmDialog group="silent-regenerate" />
    <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
            <Button
                data-cy="silent-regenerate-button"
                icon="pi pi-replay"
                severity="secondary"
                outlined
                :label="
                    running
                        ? t('participants.silentRegenerate.progress', { done: progress, total: count })
                        : t('participants.silentRegenerate.button', { count })
                "
                :loading="running"
                :disabled="!count || running"
                @click="confirmRegenerate"
            />
            <small>{{ t("participants.silentRegenerate.hint") }}</small>
        </div>
        <Message v-if="skipped.noProfile.length" severity="warn" :closable="false" data-cy="silent-regenerate-no-profile">
            {{ t("participants.silentRegenerate.noProfile", { ids: skipped.noProfile.join(", ") }) }}
        </Message>
        <Message v-if="skipped.inactive.length" severity="warn" :closable="false" data-cy="silent-regenerate-inactive">
            {{ t("participants.silentRegenerate.inactive", { ids: skipped.inactive.join(", ") }) }}
        </Message>
        <Message v-if="skipped.failed.length" severity="error" :closable="false" data-cy="silent-regenerate-failed">
            {{ t("participants.silentRegenerate.failed", { ids: skipped.failed.join(", ") }) }}
        </Message>
    </div>
</template>
